import type { PostureStatus } from '@/components/ui/posture-chip';
import {
  POSTURE_LABEL as CHART_POSTURE_LABEL,
  VERDICT_LABEL,
  VERDICT_POSTURE,
  zoneOf,
} from '@/components/ui/charts';
import type { HealthReadinessView, Recommendation } from '@/lib/api/health-readiness';
import { formatDate, formatDayMonth } from '@/lib/utils/format';

/**
 * The Health & Readiness screen's vocabulary (T-0222 / E20-F02, tabs from
 * T-0242 / E22-F01): the tabs and their URLs, the E20 `?item=` row (still
 * read, so old links land), the posture and verdict labels (the chart
 * primitives' own, in `components/ui/charts/shared.tsx`), and the score
 * band against the two thresholds. Pure, so the routes, the lists and the
 * panes agree without a store. The Overview's helpers live in
 * `overview/overview-model.ts`.
 *
 * Prototype: `docs/health-readiness/prototype/health-readiness-v2.js`.
 */

/** The seven tabs, in the strip's order (prototype `TABS`). */
export const HR_TABS = [
  { slug: 'overview', label: 'Overview' },
  { slug: 'domains', label: 'Domains' },
  { slug: 'criteria', label: 'Go-live criteria' },
  { slug: 'findings', label: 'Findings' },
  { slug: 'interviews', label: 'Interview notes' },
  { slug: 'report', label: 'Report' },
  { slug: 'snapshots', label: 'Snapshots & trend' },
] as const;

export type HrTab = (typeof HR_TABS)[number]['slug'];

export function tabLabel(tab: HrTab): string {
  return HR_TABS.find((t) => t.slug === tab)?.label ?? 'Overview';
}

/** The tab a URL shows: the first segment after `/health-readiness`. */
export function tabFromPathname(pathname: string): HrTab {
  const segment = /\/health-readiness(?:\/([^/?#]+))?/.exec(pathname)?.[1];
  const tab = HR_TABS.find((t) => t.slug === segment);
  return tab ? tab.slug : 'overview';
}

/**
 * The header's meta line (prototype `page()`): domains, go-live criteria,
 * planned go-live, last snapshot. Every count is the API's.
 */
export function headerMeta(view: HealthReadinessView): string {
  const parts = [plural(view.verdict.posture.counts.total, 'domain', 'domains')];
  const readiness = view.verdict.readiness;
  if (readiness) {
    parts.push(plural(readiness.criteriaCount, 'go-live criterion', 'go-live criteria'));
  }
  if (view.plannedGoLive) parts.push(`planned go-live ${formatDate(view.plannedGoLive)}`);
  const last = view.reports.snapshots.lastAt;
  parts.push(last ? `last snapshot ${formatDayMonth(last)}` : 'no snapshot yet');
  return parts.join(' · ');
}

export type HealthReadinessItem =
  | { kind: 'domain'; id: string }
  | { kind: 'criterion'; id: string }
  | { kind: 'findings' }
  | { kind: 'interviews' }
  | { kind: 'report' }
  | { kind: 'snapshots' };

export const STATIC_ITEMS = ['findings', 'interviews', 'report', 'snapshots'] as const;
export type StaticItemKind = (typeof STATIC_ITEMS)[number];

/** `domain:<id>` | `criterion:<id>` | findings | interviews | report | snapshots. */
export function parseItem(raw: string | null | undefined): HealthReadinessItem | null {
  if (!raw) return null;
  if ((STATIC_ITEMS as readonly string[]).includes(raw)) {
    return { kind: raw as StaticItemKind };
  }
  const colon = raw.indexOf(':');
  if (colon <= 0) return null;
  const kind = raw.slice(0, colon);
  const id = raw.slice(colon + 1);
  if (!id) return null;
  if (kind === 'domain') return { kind: 'domain', id };
  if (kind === 'criterion') return { kind: 'criterion', id };
  return null;
}

export function itemKey(item: HealthReadinessItem): string {
  if (item.kind === 'domain' || item.kind === 'criterion') return `${item.kind}:${item.id}`;
  return item.kind;
}

export const domainItem = (id: string) => `domain:${id}`;
export const criterionItem = (id: string) => `criterion:${id}`;

/**
 * The row the screen opens when the URL names none, or names one that is
 * not on the rail any more: the first domain, else the first criterion,
 * else the findings register.
 */
export function defaultItem(view: HealthReadinessView): string {
  const firstDomain = view.domains[0];
  if (firstDomain) return domainItem(firstDomain.id);
  const firstCriterion = view.criteria[0];
  if (firstCriterion) return criterionItem(firstCriterion.id);
  return 'findings';
}

/**
 * Two hints an old URL's redirect carries when it cannot know a row's id
 * (T-0224, design §4): `domain-name:<name>` opens the domain with that
 * name (the old Budget and Schedule tabs), `criterion:first` opens the
 * rail's first criterion (the old Go-Live pages). Only the screen holds
 * the composite read, so it resolves them; the redirects fetch nothing.
 */
export const FIRST_CRITERION_ITEM = 'criterion:first';
export const DOMAIN_NAME_PREFIX = 'domain-name:';
export const domainNameItem = (name: string) => `${DOMAIN_NAME_PREFIX}${name}`;

/**
 * Budget and Schedule are found by name, the same rule the domain pane
 * uses to give them the tracker and the timeline.
 */
export function sameDomainName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * The row the screen shows for the URL's `item`: the row it names, a hint
 * resolved against the rail, else the default. A hint that matches
 * nothing (no Budget domain, no criteria) falls back like a stale row.
 */
export function resolveItem(
  view: HealthReadinessView,
  raw: string | null | undefined,
): HealthReadinessItem {
  if (raw === FIRST_CRITERION_ITEM) {
    const first = view.criteria[0];
    if (first) return { kind: 'criterion', id: first.id };
  } else if (raw?.startsWith(DOMAIN_NAME_PREFIX)) {
    const name = raw.slice(DOMAIN_NAME_PREFIX.length);
    const domain = view.domains.find((d) => sameDomainName(d.name, name));
    if (domain) return { kind: 'domain', id: domain.id };
  } else {
    const parsed = parseItem(raw);
    if (parsed && itemOnRail(view, parsed)) return parsed;
  }
  // defaultItem always names a row parseItem reads.
  return parseItem(defaultItem(view))!;
}

/** True when the item names a row the rail currently shows. */
export function itemOnRail(view: HealthReadinessView, item: HealthReadinessItem): boolean {
  switch (item.kind) {
    case 'domain':
      return view.domains.some((d) => d.id === item.id);
    case 'criterion':
      return view.criteria.some((c) => c.id === item.id);
    default:
      return true;
  }
}

/** The prototype's posture words (Green / Amber / Red / Not rated): the charts' own. */
export const POSTURE_LABEL: Record<PostureStatus, string> = CHART_POSTURE_LABEL;

/** Go / Conditional / No-Go: the charts' own. */
export const RECOMMENDATION_LABEL: Record<Recommendation, string> = VERDICT_LABEL;

export function recommendationStatus(rec: Recommendation | null): PostureStatus {
  return rec ? VERDICT_POSTURE[rec] : 'unassessed';
}

/**
 * Where a 0..100 score sits against the engagement's two gates: at or
 * above Go, between Conditional and Go, or below Conditional. A
 * comparison only (the charts' `zoneOf`); the thresholds and the score
 * both come from the API.
 */
export function scoreBand(
  percent: number | null,
  thresholds: { go: number; conditional: number },
): PostureStatus {
  return zoneOf(percent, thresholds);
}

export function formatPercent(percent: number | null): string {
  if (percent === null) return '—';
  return `${Math.round(percent)}%`;
}

export function formatWeight(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

export function formatDay(iso: string | null | undefined): string {
  return iso ? formatDate(iso) : '—';
}

export function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export const BAND_TEXT_CLASS: Record<PostureStatus, string> = {
  healthy: 'text-posture-healthy',
  'at-risk': 'text-posture-at-risk',
  critical: 'text-posture-critical',
  unassessed: 'text-ink-500',
};

export const BAND_BG_CLASS: Record<PostureStatus, string> = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
  unassessed: 'bg-posture-unassessed',
};

export const BAND_RULE_CLASS: Record<PostureStatus, string> = {
  healthy: 'border-l-posture-healthy',
  'at-risk': 'border-l-posture-at-risk',
  critical: 'border-l-posture-critical',
  unassessed: 'border-l-posture-unassessed',
};
